/* eslint-disable react/jsx-no-literals */

import React, { ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IconButton } from '@mui/material';
import Tooltip from '@mui/material/Tooltip';
import styled from 'styled-components';

import BinIconSrc from '@/assets/icons/bin.svg';
import { HideFieldIcon } from '@/features/canvas/components/InitiativesAccordionContent/HideFieldIcon';
import { CUSTOM_FIELD_TITLE_MAX_LENGTH } from '@/features/canvas/constants';
import { CustomFieldType } from '@/features/canvas/constants/initiative-field';
import { SmallActionButton } from '@/features/ui/components/SmallActionButton/SmallActionButton';
import { components } from '@/types/api';

const InitiativeCustom = ({
  readOnly,
  field,
  onDeleteField,
  isLoading,
  isInCreationMode = false,
  handleSubmit,
  isInitiativeLockedAndIsNotOwner,
  children,
  extraActions,
}: {
  children: ReactNode;
  field: components['schemas']['InitiativeField'];
  handleSubmit: (title: string, type: keyof typeof CustomFieldType) => Promise<void>;
  isInitiativeLockedAndIsNotOwner: boolean;
  isLoading: boolean;
  readOnly: boolean;
  extraActions?: ReactNode;
  isInCreationMode?: boolean;
  onDeleteField?: (fieldId: number, e: React.MouseEvent<HTMLButtonElement>) => void;
}) => {
  const { t } = useTranslation();

  const [inputTitle, setInputTitle] = useState<string>(field.title || '');

  const handleInputTitleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    if (value.length <= CUSTOM_FIELD_TITLE_MAX_LENGTH) {
      setInputTitle(value);
    }
  };

  return (
    <>
      {isInCreationMode && (
        <InputWrapper>
          <StyledInput
            type="text"
            value={inputTitle}
            placeholder={t('editor.sidebar.initiative.title_custom_field_placeholder')}
            onChange={handleInputTitleChange}
            readOnly={readOnly || isInitiativeLockedAndIsNotOwner}
            maxLength={CUSTOM_FIELD_TITLE_MAX_LENGTH}
            required
          />
          <CharacterCount>
            {inputTitle.length}/{CUSTOM_FIELD_TITLE_MAX_LENGTH}
          </CharacterCount>
        </InputWrapper>
      )}
      {children}
      {!readOnly && (
        <ActionWrapper>
          {extraActions}
          {!isInCreationMode && (
            <>
              <HideFieldIcon initiativeField={field} />
              {onDeleteField && !isInitiativeLockedAndIsNotOwner && (
                <IconButton
                  aria-label="delete"
                  size="small"
                  onClick={e => onDeleteField(field.id!, e)}
                >
                  <Tooltip title={t('editor.sidebar.initiative.delete_field')} placement="top">
                    <ListStyledImage src={BinIconSrc} />
                  </Tooltip>
                </IconButton>
              )}
            </>
          )}
          {!isInitiativeLockedAndIsNotOwner && (
            <SmallActionButton
              text={t('editor.sidebar.initiative.save_button_title')}
              isLoading={isLoading}
              disabled={!inputTitle}
              onClick={() => handleSubmit(inputTitle, field.type as keyof typeof CustomFieldType)}
            />
          )}
        </ActionWrapper>
      )}
    </>
  );
};

export { InitiativeCustom };

const InputWrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 1rem;
`;

const StyledInput = styled.input`
  outline: none;
  width: 100%;
  border-radius: 0.4rem;
  border: 1px solid ${({ theme }) => theme.palette.brand.backgroundDialog};
  padding: 0 8px;
  font-size: 13px;

  &:focus {
    border: 1px solid ${({ theme }) => theme.palette.brand.textSecondary};
  }
`;

const CharacterCount = styled.span`
  font-size: 11px;
  color: ${({ theme }) => theme.palette.brand.textSecondary};
  text-align: right;
  margin-top: 4px;
`;

const ActionWrapper = styled.div`
  margin-top: 1rem;
  display: flex;
  justify-content: flex-end;
  align-items: center;
`;

export const ListStyledImage = styled.img`
  width: 100%;
  height: 100%;
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
  filter: ${({ theme }) => theme.palette.brand.textSecondaryFilter};
  &:hover {
    filter: ${({ theme }) => theme.palette.brand.buttonPrimaryFilterHover};
  }
`;
